import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Object3D } from 'three';

import { AssetManager } from '../../../../../../../viewer/asset-manager';
import { AssetType } from '../../../../../../../viewer/elements/element-obj';
import { DesignTool } from '../../../../../../../viewer/tools/design-tool';
import { isAssetPickable } from '../../../../../../../viewer/tools/select-tool';
import { Button } from '../../../../../button/button';
import { CheckBox } from '../../../../../check-box/check-box';
import {
  DataTable,
  DataTableColumns,
  DataTableSortOrder,
  DataTableSortType,
} from '../../../../../data-table/data-table';
import { Dialog } from '../../../../../dialog/dialog';
import { InfoBox } from '../../../../../info-box/info-box';
import { Stack } from '../../../../../stack/stack';
import { TextBox } from '../../../../../text-box/text-box';

type Props = {
  /**
   * What is snapped towards: the design tool, or the snapping of another tool. Its own asset, e.g.
   * the design, is not listed.
   */
  tool: Pick<DesignTool, 'excludedSnapAssetIds' | 'onExcludedSnapAssetsChanged'> & {
    asset?: Object3D;
  };
  onClose: () => void;
};

type SnapAsset = {
  id: string;
  name: string;
  type: string;
};

/**
 * Gets the name of the category the asset belongs to, the same as in the asset list.
 */
const getAssetTypeName = (t: TFunction, assetType: AssetType) => {
  switch (assetType) {
    case 'area':
      return t('area.categoryName', { ns: 'skyviewAssetMenu' });
    case 'line':
      return t('line.categoryName', { ns: 'skyviewAssetMenu' });
    case 'design':
      return t('design.categoryName', { ns: 'skyviewAssetMenu' });
    case 'dxf':
    case 'glb':
      return t('cad.categoryName', { ns: 'skyviewAssetMenu' });
    case 'model':
      return t('model.categoryName', { ns: 'skyviewAssetMenu' });
    case 'terrain-model':
      return t('terrainModel.categoryName', { ns: 'skyviewAssetMenu' });
    case 'poi':
      return t('poi.categoryName', { ns: 'skyviewAssetMenu' });
  }
};

const filterAssets = (assets: SnapAsset[], searchText: string) => {
  const text = searchText.trim().toLowerCase();

  if (text.length === 0) {
    return assets;
  }

  return assets.filter(
    (asset) => asset.name.toLowerCase().includes(text) || asset.type.toLowerCase().includes(text),
  );
};

/**
 * Lets the user choose which of the visible objects to snap towards.
 * Changes are applied directly, assets that are not shown keep their selection.
 */
export const SnapAssetsDialog = ({ tool, onClose }: Props) => {
  const { t } = useTranslation();
  const [assets] = React.useState<SnapAsset[]>(() =>
    AssetManager.instance
      .getAssets()
      .filter((asset) => asset !== tool.asset && isAssetPickable(asset))
      .map((asset) => ({
        id: asset.uuid,
        name: asset.name,
        type: getAssetTypeName(t, asset.assetType),
      })),
  );
  const [excludedIds, setExcludedIds] = React.useState(() => new Set(tool.excludedSnapAssetIds));
  const [searchText, setSearchText] = React.useState('');
  const filteredAssets = React.useMemo(
    () => filterAssets(assets, searchText),
    [assets, searchText],
  );

  const onExcludedIdsChanged = (ids: Set<string>) => {
    setExcludedIds(ids);
    tool.onExcludedSnapAssetsChanged(ids);
  };

  // Selecting all only affects the assets that match the search.
  const allSelected = filteredAssets.every((asset) => !excludedIds.has(asset.id));
  const noneSelected = filteredAssets.every((asset) => excludedIds.has(asset.id));

  const onToggle = (asset: SnapAsset) => {
    const ids = new Set(excludedIds);

    if (ids.has(asset.id)) {
      ids.delete(asset.id);
    } else {
      ids.add(asset.id);
    }

    onExcludedIdsChanged(ids);
  };

  const onToggleAll = () => {
    const ids = new Set(excludedIds);

    for (const asset of filteredAssets) {
      if (allSelected) {
        ids.add(asset.id);
      } else {
        ids.delete(asset.id);
      }
    }

    onExcludedIdsChanged(ids);
  };

  const columns: DataTableColumns<SnapAsset> = {
    checkbox: {
      title: (
        <CheckBox
          checked={allSelected ? true : noneSelected ? false : undefined}
          expandClickableArea={true}
          onChange={onToggleAll}
        />
      ),
      alignment: 'center',
      alignColumnContent: 'center',
      width: '25px',
      padding: 0,
      unfilterable: true,
      render: (asset) => (
        <CheckBox
          aria-label={asset.name}
          checked={!excludedIds.has(asset.id)}
          onChange={() => onToggle(asset)}
        />
      ),
    },
    name: {
      title: t('section.snapping.snapAssets.dialog.name', { ns: 'skyviewDesign' }),
      sortableBy: { type: DataTableSortType.STRING, sortOrder: DataTableSortOrder.ASC },
      unfilterable: true,
    },
    type: {
      title: t('section.snapping.snapAssets.dialog.type', { ns: 'skyviewDesign' }),
      sortableBy: { type: DataTableSortType.STRING },
      unfilterable: true,
    },
  };

  return (
    <Dialog maxWidth={450} onClose={onClose}>
      {{
        header: t('section.snapping.snapAssets.dialog.title', { ns: 'skyviewDesign' }),
        content:
          assets.length === 0 ? (
            <InfoBox color="blue" leftIcon={{ icon: 'info' }}>
              {t('section.snapping.snapAssets.dialog.empty', { ns: 'skyviewDesign' })}
            </InfoBox>
          ) : (
            <Stack spacing={1}>
              <span>{t('section.snapping.snapAssets.dialog.info', { ns: 'skyviewDesign' })}</span>
              <TextBox
                placeholder={t('search', { ns: 'common' })}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <DataTable
                columns={columns}
                fixedLayout={true}
                getRowKey={(asset) => asset.id}
                items={filteredAssets}
                pageSize={10}
              />
            </Stack>
          ),
        footer: {
          left: (
            <Button
              disabled={excludedIds.size === 0}
              variant="text"
              onClick={() => onExcludedIdsChanged(new Set())}
            >
              {t('section.snapping.snapAssets.dialog.reset', { ns: 'skyviewDesign' })}
            </Button>
          ),
          right: (
            <Button color="primary" variant="contained" onClick={onClose}>
              {t('close', { ns: 'common' })}
            </Button>
          ),
        },
      }}
    </Dialog>
  );
};
